import clsx from 'clsx';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';

import { useAriaLabelingProps } from '../../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import { mergeProps } from '../../../core/utils/merge-props.js';
import { useSelection } from '../hooks/useSelection.js';
import { useSpinButtonInteraction } from '../hooks/useSpinButtonInteraction.js';
import { spinButtonCSS } from '../SpinButton.sty.js';
import { NUMBER_SPIN_BUTTON_DEFAULTS } from './constants.js';
import { useBeforeInputHandler } from './hooks/useBeforeInputHandler.js';
import { useValidationAndFormatting } from './hooks/useValidationAndFormatting.js';
import { useValueNavigation } from './hooks/useValueNavigation.js';
import type { NumberSpinButtonProps, NumberSpinButtonRef } from './types.js';

/**
 * Use the NumberSpinButton component to show a number that can be changed using arrow and page keys.
 * @internal
 */
export const NumberSpinButton = /* @__PURE__ */ forwardRef<
  NumberSpinButtonRef,
  NumberSpinButtonProps
>((props, forwardedRef) => {
  const {
    disabled = false,
    readOnly = false,
    value: valueProp,
    placeholder,
    className: consumerClassName,
    style: consumerStyle,
    displayFormat,
    required,
    'data-testid': dataTestId,
    onChange,
    defaultValue = NUMBER_SPIN_BUTTON_DEFAULTS.VALUE,
    minValue,
    maxValue,
    hasError,
    onKeyDownHandler: propKeyDownHandler,
    isCyclic = false,
    'data-pattern': dataPattern,
    ...remainingProps
  }: NumberSpinButtonProps = props;

  const [behavioralTrackingProps, remainingPropsWithoutTracking] =
    useBehavioralTrackingProps(remainingProps);

  const [text, setText] = useState('');
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState(false);

  const ariaProps = useAriaLabelingProps(props);
  const rootRef = useRef<HTMLSpanElement>(null);

  const { getSelection, setSelection, selectAll, deselectAll } =
    useSelection(rootRef);
  const { validate, format, parse } = useValidationAndFormatting(
    displayFormat,
    required,
    minValue ?? NUMBER_SPIN_BUTTON_DEFAULTS.MIN_VALUE,
    maxValue ?? NUMBER_SPIN_BUTTON_DEFAULTS.MAX_VALUE,
  );

  const applyText = useCallback(
    (input: string, fireOnChange: boolean | undefined = false) => {
      const newValue = parse(input);
      setText(input);
      setValue(newValue);

      if (rootRef?.current) {
        rootRef.current.textContent = format(input);
      }
      fireOnChange && onChange?.(newValue);
    },
    [format, onChange, parse, setValue],
  );

  // Callback to clear the inner component state and a given dom element if provided.
  const clearValue = useCallback(
    (element: HTMLSpanElement | null, fireOnChange = false) => {
      setValue(null);
      setText('');
      if (element) {
        element.textContent = '';
      }
      fireOnChange && onChange?.(null);
    },
    [onChange, setValue],
  );

  const beforeInputHandler = useBeforeInputHandler(
    rootRef,
    props,
    applyText,
    text,
  );
  const { onKeyDownHandler } = useValueNavigation(
    rootRef,
    {
      value: valueProp ?? value,
      disabled,
      readOnly,
      onChange,
      displayFormat,
      required,
      minValue,
      maxValue,
      isCyclic,
      ...remainingPropsWithoutTracking,
    },
    applyText,
  );

  // Provide root element reference and functions
  useImperativeHandle(
    forwardedRef,
    (): NumberSpinButtonRef => ({
      type: 'number' as const,
      element: rootRef,
      setSelection,
      getSelection,
      selectAll,
      deselectAll,
      applyText: (input: string, fireOnChange) =>
        applyText(input, fireOnChange),
      clearValue: (fireOnChange) => clearValue(rootRef.current, fireOnChange),
    }),
    [setSelection, getSelection, selectAll, deselectAll, clearValue, applyText],
  );

  // Set error state on value change
  useEffect(() => {
    const isValid = validate(value);
    setError(!isValid);
  }, [setError, validate, value]);

  // Set initial value on first render
  useEffect(() => {
    applyText(
      defaultValue === null ||
        defaultValue === undefined ||
        Number.isNaN(defaultValue)
        ? ''
        : defaultValue.toString(),
    );
    // Only apply default value when component is mounted. Ignore subsequent defaultValue state updates.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [format]);

  // Register native beforeInput event handler. The syntethic event is not sufficient for the required features.
  useEffect(() => {
    const element = rootRef.current;
    element?.addEventListener('beforeinput', beforeInputHandler);
    return () =>
      element?.removeEventListener('beforeinput', beforeInputHandler);
  }, [beforeInputHandler]);

  // Handle internal state update if component is in controlled mode
  useEffect(() => {
    // No value prop. provided ==> component is uncontrolled
    if (valueProp === undefined) {
      return;
    }

    // Update internal state
    const sel = getSelection();
    if (valueProp !== value) {
      applyText(
        valueProp !== null && Number.isInteger(valueProp)
          ? valueProp.toString()
          : '',
      );
    }

    // Only restore the selection for the spinbutton currently focused
    document.activeElement === rootRef.current && sel && setSelection(sel);
  }, [valueProp, applyText, format, getSelection, setSelection]);

  const { onClick, onFocus, onBlur } = useSpinButtonInteraction({
    disabled,
    selectAll,
    deselectAll,
    rootElement: rootRef.current,
  });

  return (
    <span
      data-testid={dataTestId}
      ref={rootRef}
      tabIndex={disabled ? undefined : 0}
      contentEditable={
        // contentEditable when focused and non-editable when blurred, allowing text selection.
        !disabled && !readOnly ? true : undefined
      }
      spellCheck={false}
      className={clsx(
        consumerClassName,
        spinButtonCSS({ disabled, error: hasError ?? error }),
      )}
      style={
        {
          '--data-placeholder': `"${placeholder ?? ''}"`,
          ...consumerStyle,
        } as CSSProperties
      }
      aria-disabled={disabled}
      {...mergeProps(ariaProps, behavioralTrackingProps)}
      aria-label={ariaProps['aria-label'] ?? placeholder}
      role="spinbutton"
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={propKeyDownHandler ?? onKeyDownHandler}
      data-pattern={dataPattern}
    />
  );
});

/**
 * Allows to check if a ReactNode is a NumberSpinButton element.
 * @internal
 */
export function isNumberSpinButton(
  node: ReactNode,
): node is ReactElement<NumberSpinButtonProps> {
  return isSlot(node, NumberSpinButton);
}

(
  NumberSpinButton as typeof NumberSpinButton & { displayName: string }
).displayName = '_NumberSpinButton';
